import { useState } from 'react';
import {
    BookOpen, Lightbulb, AlertTriangle, CheckCircle2, ThumbsUp, MessageCircleWarning, Pencil, Trash2,
    Link2, Monitor, Wrench, Car, ChefHat, UserRound, Cog, Eye, X,
} from 'lucide-react';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import { BARVA_BG, BARVA_BORDER, TYPY, STAVY, api, datum, barva as barvaZnalosti } from './znalosti';

const IKONY_VAZEB = {
    stranka: Monitor, zarizeni: Wrench, typ_stroje: Cog, vozidlo: Car, recept: ChefHat, sop_krok: ChefHat, pozice: UserRound, clanek: BookOpen,
};

/**
 * Detail článku Znalostí — karta jednobodovky (OPL) nebo návodu.
 *
 * Jednobodovka má vždy stejné pořadí: video nebo fotka, číslované kroky
 * s fotkou, tip, Pozor, Hotovo když. Barvy boxů mají pevný význam (fialový
 * tip, oranžové Pozor, zelené Hotovo), ve všech jednobodovkách stejně.
 * Návod je volné HTML z editoru.
 *
 * Pod článkem „Pomohlo mi to / Chybí tu něco" — druhé založí úkol autorovi.
 *
 * `vSheetu`: uvnitř `SheetModal` (seznam Znalostí z lišty) — bez vlastních
 * okrajů a bez nadpisu, ten nese hlavička sheetu.
 */
export default function ClanekDetail({ clanek: c, isDark, isMobile, onZpet, onUpravit, onSmazat, onOtevritClanek, rezervaDole, vSheetu = false }) {
    const akcent = barvaZnalosti(isDark);
    const muted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const cara = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const [hodnoceni, setHodnoceni] = useState(c.moje_hodnoceni);
    const [chybiOtevreno, setChybiOtevreno] = useState(false);
    const [chybiText, setChybiText] = useState('');
    const [hlaska, setHlaska] = useState(null);
    const [odesilam, setOdesilam] = useState(false);
    const [zvetseny, setZvetseny] = useState(null);

    const jeOpl = c.typ === 'jednobodovka';
    const typ = TYPY[c.typ];

    const odeslat = async (pomohlo) => {
        setOdesilam(true); setHlaska(null);
        try {
            await api(`/api/znalosti/${c.id}/zpetna-vazba`, { method: 'POST', body: { pomohlo, text: pomohlo ? null : chybiText } });
            setHodnoceni({ pomohlo });
            setChybiOtevreno(false); setChybiText('');
            setHlaska(pomohlo ? 'Díky, že dáváš vědět.' : 'Díky. Autor dostal úkol článek doplnit.');
        } catch (e) {
            setHlaska(e.message);
        } finally {
            setOdesilam(false);
        }
    };

    const box = (druh, Icon, nadpis, text) => {
        const barvy = {
            tip: { bg: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', border: cara, ikona: akcent },
            pozor: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.40)', ikona: '#F59E0B' },
            hotovo: { bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.40)', ikona: '#10B981' },
        }[druh];
        return (
            <div className="flex" style={{ gap: 12, padding: 14, borderRadius: 14, background: barvy.bg, border: `1px solid ${barvy.border}`, marginBottom: 12 }}>
                <Icon size={20} style={{ color: barvy.ikona, flexShrink: 0, marginTop: 1 }} />
                <div className="min-w-0">
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{nadpis}</p>
                    <p style={{ margin: '3px 0 0', fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-primary)', opacity: 0.9, whiteSpace: 'pre-line' }}>{text}</p>
                </div>
            </div>
        );
    };

    const medium = (m, { vyska = 220 } = {}) => {
        if (!m) return null;
        if (m.typ === 'video') {
            return <video src={m.url} controls playsInline preload="metadata" style={{ width: '100%', maxHeight: vyska + 80, borderRadius: 12, background: '#000', display: 'block' }} />;
        }
        return (
            <button type="button" onClick={() => setZvetseny(m.url)} aria-label="Zvětšit fotku"
                style={{ display: 'block', width: '100%', padding: 0, border: `1px solid ${cara}`, borderRadius: 12, overflow: 'hidden', cursor: 'zoom-in', background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)' }}>
                <img src={m.url} alt="" loading="lazy" style={{ width: '100%', maxHeight: vyska, objectFit: 'contain', display: 'block' }} />
            </button>
        );
    };

    const sekce = (text) => (
        <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: muted, margin: '24px 2px 12px' }}>{text}</p>
    );

    const tlac = { height: isMobile ? 44 : 34, padding: '0 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent', border: `1px solid ${cara}`, color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: 6 };

    return (
        <div style={vSheetu ? { padding: '2px 2px 8px' } : { padding: isMobile ? '10px 16px 16px' : '18px 22px 22px', maxWidth: 720 }}>
            {isMobile && onZpet && <ZpetTlacitko onClick={onZpet} popis="Zpět na Znalosti" sklo isDark={isDark} style={{ marginBottom: 12 }} />}

            <div className="flex items-start" style={{ gap: 10 }}>
                <div className="flex-1 min-w-0">
                    {!vSheetu && <p className="flex items-center" style={{ gap: 6, margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: akcent }}>
                        <BookOpen size={13} /> {typ.label}{c.kod ? ` · ${c.kod}` : ''}{c.kategorie ? ` · ${c.kategorie}` : ''}
                    </p>}
                    {!vSheetu && <h1 style={{ margin: '6px 0 8px', fontSize: isMobile ? 24 : 22, fontWeight: 750, letterSpacing: '-0.4px', lineHeight: 1.2, color: 'var(--text-primary)' }}>{c.nazev}</h1>}
                    <p className="flex flex-wrap items-center" style={{ gap: 8, margin: 0, fontSize: 12.5, color: muted }}>
                        <span>verze {c.verze}</span>
                        {c.upraveno_at && <span>· {datum(c.upraveno_at)}</span>}
                        {c.kriticke && <span style={{ color: '#EF4444', fontWeight: 700 }}>· kritické</span>}
                        {c.smi_spravovat && c.stav !== 'publikovano' && (
                            <span style={{ padding: '1px 8px', borderRadius: 999, background: BARVA_BG, color: akcent, fontWeight: 700, fontSize: 11 }}>{STAVY[c.stav]}</span>
                        )}
                    </p>
                </div>
                {c.smi_spravovat && !isMobile && (
                    <div className="flex" style={{ gap: 6, flexShrink: 0 }}>
                        <button type="button" style={tlac} onClick={onUpravit}><Pencil size={13} /> Upravit</button>
                        <button type="button" style={{ ...tlac, color: '#EF4444' }} onClick={onSmazat} aria-label="Smazat"><Trash2 size={13} /></button>
                    </div>
                )}
            </div>

            {c.perex && <p style={{ margin: '14px 0 0', fontSize: 15, lineHeight: 1.5, color: 'var(--text-primary)' }}>{c.perex}</p>}

            {(c.video || c.titulni) && (
                <div style={{ marginTop: 16 }}>
                    {c.video
                        ? <video src={c.video.url} poster={c.titulni?.url} controls playsInline preload="metadata" style={{ width: '100%', maxHeight: 420, borderRadius: 14, background: '#000', display: 'block' }} />
                        : medium(c.titulni, { vyska: 320 })}
                </div>
            )}

            {jeOpl && c.kroky?.length > 0 && (
                <>
                    {sekce('Postup')}
                    {c.kroky.map((k, i) => (
                        <div key={k.id ?? i} className="flex" style={{ gap: 12, marginBottom: 20 }}>
                            <div className="grid place-items-center" style={{ width: 28, height: 28, borderRadius: 14, flexShrink: 0, background: BARVA_BG, boxShadow: `inset 0 0 0 1px ${BARVA_BORDER}`, color: akcent, fontWeight: 800, fontSize: 14 }}>{i + 1}</div>
                            <div className="flex-1 min-w-0">
                                <p style={{ margin: '3px 0 0', fontSize: 15, fontWeight: 650, lineHeight: 1.3, color: 'var(--text-primary)' }}>{k.nazev}</p>
                                {k.popis && <p style={{ margin: '3px 0 0', fontSize: 13.5, lineHeight: 1.45, color: muted, whiteSpace: 'pre-line' }}>{k.popis}</p>}
                                {k.media && <div style={{ marginTop: 10, maxWidth: 520 }}>{medium(k.media)}</div>}
                            </div>
                        </div>
                    ))}
                </>
            )}

            {!jeOpl && c.obsah_html && (
                <div className="tiptap-content" style={{ marginTop: 18, color: 'var(--text-primary)', fontSize: 14.5, lineHeight: 1.7 }}
                    dangerouslySetInnerHTML={{ __html: c.obsah_html }} />
            )}

            {(c.tip || c.pozor || c.hotovo_kdyz) && <div style={{ marginTop: jeOpl ? 4 : 20 }}>
                {c.tip && box('tip', Lightbulb, 'Tip', c.tip)}
                {c.pozor && box('pozor', AlertTriangle, 'Pozor', c.pozor)}
                {c.hotovo_kdyz && box('hotovo', CheckCircle2, 'Hotovo, když', c.hotovo_kdyz)}
            </div>}

            {(c.vazby?.filter(v => v.popis && v.objekt_typ !== 'clanek').length > 0 || c.souvisi?.length > 0) && (
                <>
                    {sekce('Souvisí')}
                    <div className="flex flex-wrap" style={{ gap: 8 }}>
                        {c.vazby.filter(v => v.popis && v.objekt_typ !== 'clanek').map((v, i) => {
                            const Icon = IKONY_VAZEB[v.objekt_typ] || Link2;
                            return (
                                <span key={`v${i}`} title={v.typ_popis} className="inline-flex items-center"
                                    style={{ gap: 6, height: 32, padding: '0 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)', border: `1px solid ${cara}` }}>
                                    <Icon size={14} style={{ color: muted }} />{v.popis}
                                </span>
                            );
                        })}
                        {c.souvisi?.map(s => (
                            <button key={`s${s.id}`} type="button" onClick={() => onOtevritClanek?.(s.id)} className="inline-flex items-center"
                                style={{ gap: 6, height: 32, padding: '0 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, color: akcent, background: BARVA_BG, border: `1px solid ${BARVA_BORDER}`, cursor: 'pointer' }}>
                                <BookOpen size={14} />{s.nazev}
                            </button>
                        ))}
                    </div>
                </>
            )}

            {c.stav === 'publikovano' && (
                <div style={{ marginTop: 24 }}>
                    {hodnoceni && !chybiOtevreno ? (
                        <p className="flex items-center" style={{ gap: 6, fontSize: 13, color: muted, margin: 0 }}>
                            <CheckCircle2 size={15} style={{ color: '#10B981' }} />
                            {hlaska || (hodnoceni.pomohlo ? 'Označil(a) jsi, že ti článek pomohl.' : 'Dal(a) jsi vědět, že tu něco chybí.')}
                        </p>
                    ) : (
                        <div className="flex" style={{ gap: 10 }}>
                            <button type="button" disabled={odesilam} onClick={() => odeslat(true)} className="flex-1 flex items-center justify-center"
                                style={{ gap: 8, height: 48, borderRadius: 12, fontSize: 14, fontWeight: 700, color: '#10B981', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.45)', cursor: 'pointer' }}>
                                <ThumbsUp size={18} /> Pomohlo mi to
                            </button>
                            <button type="button" disabled={odesilam} onClick={() => setChybiOtevreno(o => !o)} className="flex-1 flex items-center justify-center"
                                style={{ gap: 8, height: 48, borderRadius: 12, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)', border: `1px solid ${cara}`, cursor: 'pointer' }}>
                                <MessageCircleWarning size={18} /> Chybí tu něco
                            </button>
                        </div>
                    )}
                    {chybiOtevreno && (
                        <div style={{ marginTop: 10 }}>
                            <textarea value={chybiText} onChange={e => setChybiText(e.target.value)} rows={3} autoFocus
                                placeholder="Co ti v článku chybělo nebo co nesedí?"
                                style={{ width: '100%', borderRadius: 10, padding: 10, fontSize: 14, outline: 'none', resize: 'vertical', background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${cara}`, color: 'var(--text-primary)' }} />
                            <button type="button" disabled={odesilam || !chybiText.trim()} onClick={() => odeslat(false)}
                                style={{ marginTop: 8, height: 44, width: '100%', borderRadius: 12, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', background: akcent, color: '#fff', opacity: chybiText.trim() ? 1 : 0.5 }}>
                                Poslat autorovi
                            </button>
                        </div>
                    )}
                    {hlaska && !hodnoceni && <p style={{ fontSize: 12.5, color: '#EF4444', marginTop: 8 }}>{hlaska}</p>}
                </div>
            )}

            <p style={{ fontSize: 11.5, color: muted, textAlign: 'center', margin: '18px 0 0', opacity: 0.8 }}>
                {c.autor ? `Napsal(a) ${c.autor} · ` : ''}verze {c.verze}{c.revize_do ? ` · revize do ${datum(c.revize_do)}` : ''}
            </p>

            {c.smi_spravovat && c.zpetna_vazba && (
                <p className="flex items-center justify-center" style={{ gap: 12, fontSize: 11.5, color: muted, margin: '6px 0 0' }}>
                    <span className="inline-flex items-center" style={{ gap: 4 }}><Eye size={12} /> {c.zpetna_vazba.zobrazeni}</span>
                    <span className="inline-flex items-center" style={{ gap: 4 }}><ThumbsUp size={12} /> {c.zpetna_vazba.pomohlo}</span>
                    <span className="inline-flex items-center" style={{ gap: 4 }}><MessageCircleWarning size={12} /> {c.zpetna_vazba.chybi}</span>
                </p>
            )}

            {c.smi_spravovat && isMobile && (
                <div className="flex" style={{ gap: 8, marginTop: 20 }}>
                    <button type="button" style={tlac} onClick={onUpravit}><Pencil size={14} /> Upravit</button>
                    <button type="button" style={{ ...tlac, color: '#EF4444' }} onClick={onSmazat}><Trash2 size={14} /> Smazat</button>
                </div>
            )}

            {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}

            {zvetseny && (
                <div role="dialog" onClick={() => setZvetseny(null)}
                    style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.88)', display: 'grid', placeItems: 'center', padding: 12, cursor: 'zoom-out' }}>
                    <img src={zvetseny} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    <button type="button" aria-label="Zavřít" onClick={() => setZvetseny(null)}
                        style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top) + 12px)', right: 12, width: 44, height: 44, borderRadius: 22, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', display: 'grid', placeItems: 'center' }}>
                        <X size={20} />
                    </button>
                </div>
            )}
        </div>
    );
}
